import { Button } from '@repo/ui';
import { type FormEvent, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { orderErrorCode, orderErrorMessage } from '@/shared/lib';

interface OrderActionDialogProps {
  readonly title: string;
  readonly submitLabel: string;
  readonly children: ReactNode;
  readonly onSubmit: () => Promise<string | undefined>;
  /** 상태 충돌(ORDER_STATE_CONFLICT) 때만 최신 상세를 다시 읽는다. */
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
  readonly submitDisabled?: boolean;
  readonly validate?: () => string | null;
}
/**
 * 관리자 주문 처리 대화상자 — 네이티브 `<dialog>` 모달(포커스를 안으로 옮기고 바깥을 inert 로 만든다). 모양은 develop 이름 입력
 * 모달(NameFormModal)과 같다: 어둡게 깐 뒤 배경, 흰 판·얇은 테두리·그림자, 작은 제목, 오른쪽 아래 [닫기][주 동작] 버튼.
 * preflight 가 지운 가운데 정렬은 m-auto 로 되살린다.
 * - Escape(cancel)는 기본 닫힘을 막고 처리 중이 아닐 때만 닫는다.
 * - 브라우저가 cancel 없이 직접 닫은 경우(처리 중 Escape 를 거듭 누름)는 `onClose` 로 받아, 처리 중이면 다시 모달로 띄워 결과
 *   안내가 사라지지 않게 하고, 아니면 화면 상태에서도 닫는다.
 * - 열기 직전 포커스가 있던 요소(여는 버튼)를 기억했다가 닫히면(언마운트) 그 요소로 포커스를 돌려준다.
 */
export function OrderActionDialog({
  title,
  submitLabel,
  children,
  onSubmit,
  onReload,
  onClose,
  pending = false,
  submitDisabled = false,
  validate,
}: OrderActionDialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const lock = useRef(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);
  // 레이아웃 단계에서 열고 닫는다 — 언마운트 때 DOM 에서 떼어 내기 전에 닫아야 브라우저가 모달 상태를 정리한다.
  useLayoutEffect(() => {
    const node = dialog.current;
    if (!node) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node.showModal();
    return () => {
      node.close();
      // 렌더가 끝난 뒤 돌려준다 — 같은 렌더에서 다시 활성화되는 버튼도 받는다.
      if (opener)
        queueMicrotask(() => {
          if (opener.isConnected) opener.focus();
        });
    };
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending || submitDisabled || lock.current || complete) return;
    const invalid = validate?.();
    if (invalid) {
      setMessage(invalid);
      return;
    }
    lock.current = true;
    setSaving(true);
    setMessage(null);
    try {
      // 성공 뒤 상세는 쓰기 훅이 갱신한다(응답 상세를 캐시에 넣거나 상세를 무효화) — 여기서 또 읽으면 개인정보 접속기록이 겹친다.
      const result = await onSubmit();
      if (result) {
        setMessage(result);
        setComplete(true);
      } else onClose();
    } catch (error) {
      setMessage(orderErrorMessage(error));
      if (orderErrorCode(error) === 'ORDER_STATE_CONFLICT') onReload();
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      aria-label={title}
      aria-modal="true"
      className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-lg border bg-background p-5 text-foreground shadow-lg backdrop:bg-foreground/40"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving && !pending) onClose();
      }}
      onClose={() => {
        const node = dialog.current;
        if (!node || node.open) return;
        if (saving || pending) node.showModal();
        else onClose();
      }}
    >
      <h2 className="font-medium">{title}</h2>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <fieldset
          disabled={saving || pending || complete}
          className="flex min-w-0 flex-col gap-3 text-sm"
        >
          {children}
        </fieldset>
        {message && (
          <p
            role={complete ? 'status' : 'alert'}
            className={
              complete
                ? 'rounded-xl bg-foreground/[0.04] px-4 py-3 text-sm font-medium'
                : 'text-sm text-destructive'
            }
          >
            {message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={saving || pending}
            onClick={onClose}
          >
            닫기
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={saving}
            disabled={pending || complete || submitDisabled}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
